import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  DeviceRoles,
  Feature,
  FeatureNames,
  GatewayRemoteDiagnosticsStatus,
  RemoteDiagnosticsCommands,
  RemoteDiagnosticsProperties,
} from '@vi-iot/client';

import {
  ActiveErrorsSelectors,
  AuthSelectors,
  DIAGNOSTICS_CONNECTION_STATUS,
  ExtendedParametersTabsEnum,
  InstallationDetailsSelectors,
  isReady,
  selectQueryParams,
} from '@store';

import { AppState } from '../app.state';
import { AuthState } from '../auth';
import { BackendStorageState } from '../settings';
import {
  matchVdd,
  RawVddDetails,
  VddDetailsListSelectors,
  VddDetailsListState,
} from '../vdd-details-list';

import { DiagnosticsOverlayTypeEnum } from '../../+installations/installation/installation-overlay.model';
import {
  ActiveErrorWithDiagStackDetail,
  backendStorageSessionsKey,
  DIAGNOSTICS_CONNECTION_STEP,
  HydraulicSchemeParams,
  localStorageSessionsKey,
  RemoteDiagnosticsActiveSession,
  RemoteDiagnosticsControlUnit,
  RemoteDiagnosticsParameter,
  RemoteDiagnosticsVddListParameter,
  SetupInfo,
  vddListAmountLimit,
  XAuthTokenAndDiagStackNode,
  DiagStackConnectionProperties,
  vddToHide,
  RemoteDiagnosticsTab,
} from './remote-diagnostics.model';
import { RemoteDiagnosticsState } from './remote-diagnostics.state';
import { InstallationFeaturesSelectors } from '../installation-features/installation-features.selectors';

export namespace RemoteDiagnosticsSelectors {
  const remoteDiagnostics = (state: AppState): RemoteDiagnosticsState => state.remoteDiagnostics;
  const backendStorageState = (state: AppState): BackendStorageState => state.backendStorage;
  const auth = (state: AppState): AuthState => state.auth;
  export const vddDetailsList = (state: AppState): VddDetailsListState => state.vddDetailsList;

  export const activeSessionStatusChecked = createSelector(
    remoteDiagnostics,
    (state: RemoteDiagnosticsState): boolean => state.activeSessionStatusChecked
  );
  export const selectLogicalLinkOpenLoading = createSelector(
    remoteDiagnostics,
    (state: RemoteDiagnosticsState): boolean => state.logicalLinkOpenStatus.loading
  );
  export const selectVddListLoading = createSelector(
    remoteDiagnostics,
    (state: RemoteDiagnosticsState): boolean => state.vddListStatus.loading
  );

  export const selectConfirmPopupState = createSelector(
    remoteDiagnostics,
    (state: RemoteDiagnosticsState): boolean => {
      return state.isUseCasePopupBlocked;
    }
  );

  export const selectIsChartUncollapsed = createSelector(
    remoteDiagnostics,
    (state: RemoteDiagnosticsState): boolean => {
      return state.isChartUncollapsed;
    }
  );

  export function isLoading(): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(remoteDiagnostics, (state: RemoteDiagnosticsState) => state.loading);
  }
  export function isSessionStatusChecked(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      remoteDiagnostics,
      (state: RemoteDiagnosticsState) => state.sessionStatusChecked
    );
  }

  export function getActiveConnectionStep(): MemoizedSelector<
    AppState,
    DIAGNOSTICS_CONNECTION_STEP,
    DefaultProjectorFn<DIAGNOSTICS_CONNECTION_STEP>
  > {
    return createSelector(
      remoteDiagnostics,
      (state: RemoteDiagnosticsState) => state?.activeConnectionStep
    );
  }

  export function getConnectionStatus(): MemoizedSelector<
    AppState,
    DIAGNOSTICS_CONNECTION_STATUS,
    DefaultProjectorFn<DIAGNOSTICS_CONNECTION_STATUS>
  > {
    return createSelector(
      remoteDiagnostics,
      (state: RemoteDiagnosticsState) => state.connectionStatus
    );
  }

  export function getActiveSessions(): MemoizedSelector<
    AppState,
    RemoteDiagnosticsActiveSession[],
    DefaultProjectorFn<RemoteDiagnosticsActiveSession[]>
  > {
    return createSelector(
      remoteDiagnostics,
      (state: RemoteDiagnosticsState) => state?.activeSessions
    );
  }

  export function getConnectionReason(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      remoteDiagnostics,
      (state: RemoteDiagnosticsState) => state?.connectionReason
    );
  }

  export function getLogicalLinkList(): MemoizedSelector<
    AppState,
    string[],
    DefaultProjectorFn<string[]>
  > {
    return createSelector(
      remoteDiagnostics,
      (state: RemoteDiagnosticsState) => state?.logicalLinkList
    );
  }

  export function isActiveSessionAvailable(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics
      ),
      (
        remoteDiagnosticsState: RemoteDiagnosticsState,
        remoteDiagnosticsFeature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>
      ) => {
        const localStorageKey = localStorage.getItem(`${localStorageSessionsKey}${gatewaySerial}`);
        const featureStatusActive =
          remoteDiagnosticsFeature?.properties?.status?.value ===
          GatewayRemoteDiagnosticsStatus.Active;
        const activeSession = remoteDiagnosticsState?.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        );

        return !!(activeSession && featureStatusActive && localStorageKey);
      }
    );
  }

  export function isActiveSessionAvailableBasedOnStatusFeatureOnly(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics
      ),
      (
        remoteDiagnosticsFeature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>
      ) => {
        const featureStatusActive =
          remoteDiagnosticsFeature?.properties?.status?.value ===
          GatewayRemoteDiagnosticsStatus.Active;

        return !!featureStatusActive;
      }
    );
  }

  export function getSelectedControlUnit(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsControlUnit,
    DefaultProjectorFn<RemoteDiagnosticsControlUnit>
  > {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.selectedControlUnit
    );
  }

  export function getDiagStackNode(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.diagStackNode
    );
  }

  export function getSessionGuid(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.sessionGuid
    );
  }

  export function getDiagStackSetupInfo(
    gatewaySerial: string
  ): MemoizedSelector<AppState, SetupInfo, DefaultProjectorFn<SetupInfo>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.setupInfo
    );
  }

  export function getControlUnits(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsControlUnit[],
    DefaultProjectorFn<RemoteDiagnosticsControlUnit[]>
  > {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.availableControlUnits
    );
  }

  export function getMainLogicalLinkFromTopology(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.mainLogicalLinkFromTopology
    );
  }

  export function getBundleIdFromTopologyAndHydrauliMatrixVddDetails(
    gatewaySerial: string,
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, HydraulicSchemeParams, DefaultProjectorFn<HydraulicSchemeParams>> {
    return createSelector(
      RemoteDiagnosticsSelectors.getBundleIdFromTopology(gatewaySerial),
      VddDetailsListSelectors.getVddDetail(vddName, logicalLink),
      (bundleId: string, hydraulicMatrixVddDetails: RawVddDetails) => {
        const vdd = hydraulicMatrixVddDetails?.singleRead;

        return { bundleId, hydraulicMatrixVddDetails: vdd };
      }
    );
  }

  export function getBundleIdFromTopology(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) => {
      const bundleId = remoteDiagnosticsState.activeSessions.find(
        (session) => session.gatewaySerial === gatewaySerial
      )?.bundleId;

      return bundleId;
    });
  }

  export function getIntervals(): MemoizedSelector<
    AppState,
    number[],
    DefaultProjectorFn<number[]>
  > {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) => remoteDiagnosticsState.supportedIntervals
    );
  }

  export function getAvailableParameters(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsVddListParameter[],
    DefaultProjectorFn<RemoteDiagnosticsVddListParameter[]>
  > {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.availableParametersForSelectedControlUnit
    );
  }

  export function getAvailableParametersExceptSelected(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsVddListParameter[],
    DefaultProjectorFn<RemoteDiagnosticsVddListParameter[]>
  > {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) => {
      const activeSession = remoteDiagnosticsState.activeSessions.find(
        (session) => session.gatewaySerial === gatewaySerial
      );

      return activeSession?.availableParametersForSelectedControlUnit?.filter(
        (availableParameters) =>
          !activeSession?.selectedParameters.some(
            (parameter) =>
              parameter.vddDetail.name === availableParameters.name &&
              parameter.controlUnit.value === activeSession.selectedControlUnit.value
          ) && availableParameters.name !== vddToHide
      );
    });
  }

  export function isParameterRunning(
    gatewaySerial: string,
    parameterId: number
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) => {
      const selectedParameters = remoteDiagnosticsState.activeSessions.find(
        (session) => session.gatewaySerial === gatewaySerial
      )?.selectedParameters;

      return !!selectedParameters?.find((parameter) => parameter?.id === parameterId)?.requestId;
    });
  }

  export function getSelectedParameters(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsParameter[],
    DefaultProjectorFn<RemoteDiagnosticsParameter[]>
  > {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) => {
      const activeSession = remoteDiagnosticsState.activeSessions.find(
        (session) => session.gatewaySerial === gatewaySerial
      );
      if (activeSession.selectedExtendedParametersTab === ExtendedParametersTabsEnum.UseCases) {
        return activeSession.selectedParameters.filter((param) => param.vddDetail.isUseCaseVdd);
      }
      if (
        activeSession.selectedExtendedParametersTab ===
        ExtendedParametersTabsEnum.SearchedParameters
      ) {
        return activeSession.selectedParameters.filter((param) => !param.vddDetail.isUseCaseVdd);
      }
    });
  }

  export function getSelectedParametersSelectedOnly(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsParameter[],
    DefaultProjectorFn<RemoteDiagnosticsParameter[]>
  > {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) =>
      remoteDiagnosticsState.activeSessions
        .find((session) => session.gatewaySerial === gatewaySerial)
        ?.selectedParameters.filter((parameter) => parameter.isSelected)
    );
  }

  export function getSelectedParameterStartedAndSelected(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsParameter[],
    DefaultProjectorFn<RemoteDiagnosticsParameter[]>
  > {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) =>
      remoteDiagnosticsState.activeSessions
        .find((session) => session.gatewaySerial === gatewaySerial)
        ?.selectedParameters.filter(
          (parameter) => parameter.requestId !== '' && parameter.isSelected
        )
    );
  }

  export function getSelectedParameterStartedOrSelected(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsParameter[],
    DefaultProjectorFn<RemoteDiagnosticsParameter[]>
  > {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) =>
      remoteDiagnosticsState.activeSessions
        .find((session) => session.gatewaySerial === gatewaySerial)
        ?.selectedParameters.filter(
          (parameter) => parameter.requestId !== '' || parameter.isSelected
        )
    );
  }

  export function getSelectedParametersStoppedAndSelectedNotInError(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsParameter[],
    DefaultProjectorFn<RemoteDiagnosticsParameter[]>
  > {
    return createSelector(
      remoteDiagnostics,
      vddDetailsList,
      (remoteDiagnosticsState: RemoteDiagnosticsState, vddDetailsListState: VddDetailsListState) =>
        remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.filter(
            (parameter) => parameter.requestId === '' && parameter.isSelected
          )
          .filter((parameter) => {
            const vdd = vddDetailsListState.vddDetailsList.find((vddDetailsItem) =>
              matchVdd(vddDetailsItem, parameter.vddDetail.name, parameter.controlUnit.value)
            );

            return (
              !vdd?.metadataLoading &&
              !vdd?.metadataError &&
              !vdd?.structureLoading &&
              !vdd?.structureError &&
              !vdd?.singleReadLoading &&
              !vdd?.singleReadError
            );
          })
    );
  }

  export function isAnyParameterLoading(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      vddDetailsList,
      (remoteDiagnosticsState: RemoteDiagnosticsState, vddDetailsListState: VddDetailsListState) =>
        !!remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.some((parameter) => {
            const vdd = vddDetailsListState.vddDetailsList.find((vddDetailsItem) =>
              matchVdd(vddDetailsItem, parameter.vddDetail.name, parameter.controlUnit.value)
            );

            return vdd?.metadataLoading || vdd?.structureLoading || vdd?.singleReadLoading;
          })
    );
  }

  export function getSelectedParametersLogicalLink(
    gatewaySerial: string,
    vddName: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters?.find((parameter) => parameter.vddDetail.name === vddName)
          ?.controlUnit.value
    );
  }

  export function getSelectedParameterIsWriteFlag(
    gatewaySerial: string,
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters?.find(
            (parameter) =>
              parameter.vddDetail.name === vddName && parameter.controlUnit.value === logicalLink
          )?.vddDetail?.isWrite
    );
  }

  export function getSelectedParameterIsReadFlag(
    gatewaySerial: string,
    vddName: string,
    logicalLink: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) => {
      const activeSession = remoteDiagnosticsState.activeSessions.find(
        (session) => session.gatewaySerial === gatewaySerial
      );
      const selectedParameter = activeSession?.selectedParameters?.find(
        (parameter) =>
          parameter.vddDetail.name === vddName && parameter.controlUnit.value === logicalLink
      );

      return selectedParameter?.vddDetail?.isRead;
    });
  }

  export function isAnyParameterRunning(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        !!remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.find((parameter) => parameter.requestId !== '')
    );
  }

  export function isAnyParameterStoppedAndSelected(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        !!remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.find(
            (parameter) => parameter.requestId === '' && parameter.isSelected
          )
    );
  }

  export function isAnyParameterStoppedAndSelectedAndNotInError(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      vddDetailsList,
      (remoteDiagnosticsState: RemoteDiagnosticsState, vddDetailsListState: VddDetailsListState) =>
        !!remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.filter((parameter) => {
            const vdd = vddDetailsListState.vddDetailsList.find((vddDetailsItem) =>
              matchVdd(vddDetailsItem, parameter.vddDetail.name, parameter.controlUnit.value)
            );

            return (
              !vdd?.metadataLoading &&
              !vdd?.metadataError &&
              !vdd?.structureLoading &&
              !vdd?.structureError &&
              !vdd?.singleReadLoading &&
              !vdd?.singleReadError
            );
          })
          .find((parameter) => parameter.requestId === '' && parameter.isSelected)
    );
  }

  export function isAnyParameterRunningAndSelected(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        !!remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.find(
            (parameter) => parameter.requestId !== '' && parameter.isSelected
          )
    );
  }

  export function isAnyParameterSelected(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        !!remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.find((parameter) => parameter.isSelected)
    );
  }

  export function isAnyParameterNotSelected(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        !!remoteDiagnosticsState.activeSessions
          .find((session) => session.gatewaySerial === gatewaySerial)
          ?.selectedParameters.find((parameter) => !parameter.isSelected)
    );
  }

  export function isParameterListEmpty(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        !remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.selectedParameters.length
    );
  }

  export function getParametersWhichAreBeingRead(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    RemoteDiagnosticsParameter[],
    DefaultProjectorFn<RemoteDiagnosticsParameter[]>
  > {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) =>
      remoteDiagnosticsState.activeSessions
        .find((session) => session.gatewaySerial === gatewaySerial)
        ?.selectedParameters.filter((p) => p.requestId !== '')
    );
  }

  export function isParameterListFull(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) => {
      return (
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.selectedParameters.length >= vddListAmountLimit
      );
    });
  }

  export function getXAuthToken(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.xAuthToken?.session
    );
  }

  export function getLastUserActivityTimestamp(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.lastUserActivityTimestamp
    );
  }

  export function getTechnicianWsUrl(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.technicianWsUrl
    );
  }

  export function getVciName(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.vciName
    );
  }

  export function getVinNumber(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      selectQueryParams,
      (remoteDiagnosticsState: RemoteDiagnosticsState, queryParams) => {
        const selectedVin = queryParams['deviceSerial'];
        if (selectedVin) {
          return selectedVin;
        } else {
          return remoteDiagnosticsState.activeSessions.find(
            (session) => session.gatewaySerial === gatewaySerial
          )?.vinNumber;
        }
      }
    );
  }

  export function getConnectedDeviceSerial(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.deviceSerial
    );
  }

  export function getVciResource(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.vciResource
    );
  }

  export function getActiveLogicalLinks(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string[], DefaultProjectorFn<string[]>> {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState) =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.activeLogicalLinkNames
    );
  }

  export function getLogicalLinkOfSelectedActiveError(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      ActiveErrorsSelectors.getSelectedActiveError,
      (remoteDiagnosticsState: RemoteDiagnosticsState, selectedActiveError) => {
        const selectedErrorCode = selectedActiveError.errorCode;
        const activeSession = remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        );

        if (activeSession) {
          const logicalLink = activeSession.busDetailsFromTopology.find(
            (busDetails) => busDetails.errorCode === selectedErrorCode
          )?.logicalLink;

          return logicalLink ?? activeSession.mainLogicalLinkFromTopology;
        }
      }
    );
  }

  export function getNameOfSelectedActiveError(
    gatewaySerial: string
  ): MemoizedSelector<AppState, string, DefaultProjectorFn<string>> {
    return createSelector(
      remoteDiagnostics,
      ActiveErrorsSelectors.getSelectedActiveError,
      (remoteDiagnosticsState: RemoteDiagnosticsState, selectedActiveError) => {
        const selectedErrorCode = selectedActiveError.errorCode;
        const activeSession = remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        );

        return activeSession.busDetailsFromTopology.find(
          (busDetails) => busDetails.errorCode === selectedErrorCode
        )?.errorName;
      }
    );
  }

  export function isActiveSessionInBackendStorage(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      backendStorageState,
      auth,
      (state: BackendStorageState, authState: AuthState) => {
        const userLoginId = authState?.me?.loginId;
        const keyValue = state.keys.find((k) => k[backendStorageSessionsKey]);

        if (
          keyValue &&
          keyValue[backendStorageSessionsKey]?.activeSessions?.length &&
          gatewaySerial
        ) {
          const isActiveSession = keyValue[backendStorageSessionsKey].activeSessions.find(
            (session) => session.gatewaySerial === gatewaySerial && session.user === userLoginId
          );

          return !!isActiveSession;
        } else {
          return false;
        }
      }
    );
  }

  export function isActiveSessionOpenedOnAnotherBrowser(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(remoteDiagnostics, (remoteDiagnosticsState: RemoteDiagnosticsState) => {
      const localStorageKey = localStorage.getItem(`${localStorageSessionsKey}${gatewaySerial}`);
      const activeSession = remoteDiagnosticsState.activeSessions.some(
        (session) => session.gatewaySerial === gatewaySerial
      );

      return !localStorageKey && activeSession;
    });
  }

  export function isDiagnosticsSessionRunning(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      RemoteDiagnosticsSelectors.isActiveSessionOpenedOnAnotherBrowser(gatewaySerial),
      RemoteDiagnosticsSelectors.hasRestrictedAccessToActiveDiagnosticsSession(gatewaySerial),
      RemoteDiagnosticsSelectors.getConnectionStatus(),
      AuthSelectors.isDemo,
      (
        isSessionOpenedOnAnotherBrowser,
        hasRestrictedAccessToDiagnosticsSession,
        connectionStatus: DIAGNOSTICS_CONNECTION_STATUS,
        isDemo
      ) =>
        connectionStatus === DIAGNOSTICS_CONNECTION_STATUS.INACTIVE &&
        ((isSessionOpenedOnAnotherBrowser && !isDemo) || hasRestrictedAccessToDiagnosticsSession)
    );
  }

  export function isDiagnosticsSessionInactive(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      RemoteDiagnosticsSelectors.isActiveSessionOpenedOnAnotherBrowser(gatewaySerial),
      RemoteDiagnosticsSelectors.hasRestrictedAccessToActiveDiagnosticsSession(gatewaySerial),
      RemoteDiagnosticsSelectors.getConnectionStatus(),
      AuthSelectors.isDemo,
      (
        isSessionOpenedOnAnotherBrowser,
        hasRestrictedAccessToDiagnosticsSession,
        connectionStatus: DIAGNOSTICS_CONNECTION_STATUS,
        isDemo
      ) =>
        connectionStatus === DIAGNOSTICS_CONNECTION_STATUS.INACTIVE &&
        (!isSessionOpenedOnAnotherBrowser || isDemo) &&
        !hasRestrictedAccessToDiagnosticsSession
    );
  }

  export function hasRestrictedAccessToActiveDiagnosticsSession(
    gatewaySerial: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      RemoteDiagnosticsSelectors.isActiveSessionInBackendStorage(gatewaySerial),
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(
        FeatureNames.gatewayRemoteDiagnostics
      ),
      AuthSelectors.isRsaUser(),
      AuthSelectors.hasUserRolesForRemoteDiagnosis(),
      (
        isActiveRemoteDiagSessionInBackendStorage,
        remoteDiagnosticsFeature: Feature<RemoteDiagnosticsProperties, RemoteDiagnosticsCommands>,
        isRsa: boolean,
        isUserAuthorizedToUseDiagnostics
      ) => {
        const isDiagnosticsReady = isReady(remoteDiagnosticsFeature);
        const isRemoteDiagnosticsActive =
          remoteDiagnosticsFeature?.properties?.status?.value ===
          GatewayRemoteDiagnosticsStatus.Active;

        const userDidNotCreateCurrentActiveSession =
          !isActiveRemoteDiagSessionInBackendStorage &&
          isDiagnosticsReady &&
          isRemoteDiagnosticsActive;
        const rsaWithoutAccess = isRsa && !isUserAuthorizedToUseDiagnostics;

        return false;
        return userDidNotCreateCurrentActiveSession || rsaWithoutAccess;
      }
    );
  }

  export function getBusDetailsFromTopology(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    ActiveErrorWithDiagStackDetail[],
    DefaultProjectorFn<ActiveErrorWithDiagStackDetail[]>
  > {
    return createSelector(
      remoteDiagnostics,
      (remoteDiagnosticsState: RemoteDiagnosticsState): ActiveErrorWithDiagStackDetail[] =>
        remoteDiagnosticsState.activeSessions.find(
          (session) => session.gatewaySerial === gatewaySerial
        )?.busDetailsFromTopology
    );
  }

  export function isHydraulicSchemaAvailable(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      InstallationDetailsSelectors.isInstallationVitocrossalNAType(),
      InstallationDetailsSelectors.isMainDeviceVitocharge(),
      (isVitocrossal: boolean, isVitocharge: boolean) => !isVitocrossal && !isVitocharge
    );
  }

  export const scheduleMultipleParametersReadCycleInProgress = createSelector(
    remoteDiagnostics,
    (state: RemoteDiagnosticsState): boolean => state.scheduleMultipleParametersReadCycleInProgress
  );

  export function getDiagStackConnectionProperties(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    DiagStackConnectionProperties,
    DefaultProjectorFn<DiagStackConnectionProperties>
  > {
    return createSelector(
      RemoteDiagnosticsSelectors.getDiagStackNode(gatewaySerial),
      RemoteDiagnosticsSelectors.getXAuthToken(gatewaySerial),
      RemoteDiagnosticsSelectors.getVciName(gatewaySerial),
      RemoteDiagnosticsSelectors.getVciResource(gatewaySerial),
      (node, xAuthToken, vciName, vciResource) => {
        return {
          node,
          xAuthToken,
          vciName,
          vciResource,
        };
      }
    );
  }

  export function getXAuthTokenAndDiagStackNode(
    gatewaySerial: string
  ): MemoizedSelector<
    AppState,
    XAuthTokenAndDiagStackNode,
    DefaultProjectorFn<XAuthTokenAndDiagStackNode>
  > {
    return createSelector(
      RemoteDiagnosticsSelectors.getDiagStackNode(gatewaySerial),
      RemoteDiagnosticsSelectors.getXAuthToken(gatewaySerial),
      (node, xAuthToken) => {
        return {
          node,
          xAuthToken,
        };
      }
    );
  }

  export const cancelMultipleParametersReadCycleInProgress = createSelector(
    remoteDiagnostics,
    (state: RemoteDiagnosticsState): boolean => state.cancelMultipleParametersReadCycleInProgress
  );

  export const selectRemoteDiagnosticsTabs = (
    gatewaySerial: string
  ): MemoizedSelector<AppState, RemoteDiagnosticsTab[]> =>
    createSelector(
      RemoteDiagnosticsSelectors.getMainLogicalLinkFromTopology(gatewaySerial),
      RemoteDiagnosticsSelectors.isHydraulicSchemaAvailable(),
      InstallationDetailsSelectors.hasSelectedDeviceGivenRoles([DeviceRoles.BackupCapability]),
      InstallationDetailsSelectors.isSelectedGatewayAV2Cascade,
      (
        mainLogicalLinkFromTopology,
        isHydraulicSchemaAvailableValue,
        hasSelectedDeviceGivenRoles,
        isSelectedGatewayAV2Cascade
      ) => {
        let diagnosticsOverlayTabs: RemoteDiagnosticsTab[] = [
          {
            type: DiagnosticsOverlayTypeEnum.DiagnosisBackupAndRestore,
            title: '+INSTALLATIONS.INSTALLATION.MENU_BAR.DIAGNOSIS.BACKUP.TITLE',
            active: true,
          },
          {
            type: DiagnosticsOverlayTypeEnum.ExtendedParameter,
            title: '+INSTALLATIONS.INSTALLATION.MENU_BAR.DIAGNOSIS.EXTENDED_PARAMETERS.TITLE',
            active: true,
          },
          {
            type: DiagnosticsOverlayTypeEnum.GuidedTroubleshooting,
            title: '+INSTALLATIONS.INSTALLATION.MENU_BAR.GTS.TITLE',
            active: true,
          },
          {
            type: DiagnosticsOverlayTypeEnum.HydraulicSchematic,
            title: '+INSTALLATIONS.INSTALLATION.MENU_BAR.HYDRAULIC_SCHEME.TITLE',
            active: true,
          },
          {
            type: DiagnosticsOverlayTypeEnum.DiagnosisSnapshots,
            title: '+INSTALLATIONS.INSTALLATION.MENU_BAR.DIAGNOSIS.SNAPSHOT.TITLE',
            active: true,
          },
        ];

        if (!isHydraulicSchemaAvailableValue) {
          const hydraulicSchematicTab = diagnosticsOverlayTabs.find(
            (tab) => tab.type === DiagnosticsOverlayTypeEnum.HydraulicSchematic
          );
          hydraulicSchematicTab.active = false;
        }

        if (!mainLogicalLinkFromTopology) {
          diagnosticsOverlayTabs
            .filter(
              (tab) =>
                tab.type === DiagnosticsOverlayTypeEnum.GuidedTroubleshooting ||
                tab.type === DiagnosticsOverlayTypeEnum.HydraulicSchematic ||
                tab.type === DiagnosticsOverlayTypeEnum.DiagnosisBackupAndRestore
            )
            .map((tab) => {
              tab.active = false;

              return tab;
            });
        }

        if (!hasSelectedDeviceGivenRoles) {
          const backupTab = diagnosticsOverlayTabs.find(
            (tab) => tab.type === DiagnosticsOverlayTypeEnum.DiagnosisBackupAndRestore
          );
          if (backupTab) {
            backupTab.active = false;
          }
        }

        if (isSelectedGatewayAV2Cascade) {
          diagnosticsOverlayTabs = diagnosticsOverlayTabs.filter(
            (tab) =>
              tab.type === DiagnosticsOverlayTypeEnum.DiagnosisBackupAndRestore ||
              tab.type === DiagnosticsOverlayTypeEnum.DiagnosisSnapshots ||
              tab.type === DiagnosticsOverlayTypeEnum.HydraulicSchematic
          );
          diagnosticsOverlayTabs.forEach((tab) => (tab.active = false));
        }

        return diagnosticsOverlayTabs;
      }
    );
}
